/**
 * Phase2RailHeader.tsx
 * The SINGLE, sticky, merged WEEK + day header for the Phase-2 rail (2026-06-23).
 *
 * One header shared by the Task grid + the Capacity grid below it, so the week labels:
 *   - line up with BOTH grids (rendered as its own mini-table with the same column
 *     widths + border model → no pixel drift against the bordered grids), and
 *   - stay pinned at the top across BOTH grids while you scroll a tall plan (a per-grid
 *     sticky header only stuck for its own grid and vanished in the gap between them).
 *
 * Row 1 = WEEK n + date range (aligns with the task grid's per-week cells). Row 2 = the
 * Sa–Fr day labels (aligns with the capacity grid's per-day cells). It scrolls
 * horizontally with the rail and feeds the shared hover "beam".
 */

import React from 'react';
import { Box, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { fmtWeekRange } from '../../../../utils/weekUtils';
import { RAIL_GUTTER_W, RAIL_DAY_W, RAIL_WEEK_W } from './phase2RailGeometry';

// BST week is Saturday→Friday: day 0 = Sat, 1 = Sun (weekend), 2–6 = Mon–Fri.
const DAY_LABELS = ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'] as const;

function addDaysISO(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysISO(weekStart, i));
}

export interface Phase2RailHeaderProps {
  weekColumns: string[];
  holidays?: readonly string[];
  hoveredWeek?: string | null;
  onHoverWeek?: (weekStart: string | null) => void;
}

export const Phase2RailHeader: React.FC<Phase2RailHeaderProps> = ({
  weekColumns,
  holidays = [],
  hoveredWeek,
  onHoverWeek,
}) => {
  const theme = useTheme();
  const holidaySet = React.useMemo(() => new Set(holidays), [holidays]);
  if (weekColumns.length === 0) return null;
  const beamRail = `2px solid ${theme.schedule.selection}`;

  return (
    <Box
      onMouseLeave={() => onHoverWeek?.(null)}
      sx={{
        // Sticks just under the fixed app bar (the main content area is the vertical
        // scroll container). High z so it floats above BOTH grids' content. Its
        // containing block is the whole rail, so it stays stuck across both grids.
        position: 'sticky',
        top: 0,
        zIndex: 20,
        width: 'fit-content',
        border: `1px solid ${theme.border.color}`,
        borderRadius: `${theme.border.radius}px`,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 3px 8px -3px ${alpha(theme.palette.common.black, 0.25)}`,
      }}
    >
      <Table
        size="small"
        sx={{ tableLayout: 'fixed', width: RAIL_GUTTER_W + weekColumns.length * RAIL_WEEK_W }}
      >
        <TableHead>
          {/* Row 1 — gutter (sticky-left) + one WEEK cell per week. */}
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: theme.table.headerBg,
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: 0.3,
                width: RAIL_GUTTER_W,
                py: 1,
                px: 1.5,
                borderBottom: `2px solid ${theme.border.color}`,
                // Frozen-column right divider via inset shadow (a plain borderRight is
                // collapsed away by the table when content scrolls under it). The soft
                // outer shadow reads as "frozen" (2026-06-24).
                boxShadow: `inset -1px 0 0 0 ${theme.border.color}, 6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
                verticalAlign: 'bottom',
              }}
            >
              Task / Employee
            </TableCell>
            {weekColumns.map((ws, idx) => {
              const hot = hoveredWeek === ws;
              return (
                <TableCell
                  key={ws}
                  align="center"
                  colSpan={7}
                  onMouseEnter={() => onHoverWeek?.(ws)}
                  sx={{
                    backgroundColor: hot ? alpha(theme.schedule.selection, 0.14) : theme.table.headerBg,
                    py: 0.5,
                    px: 0.5,
                    width: RAIL_WEEK_W,
                    borderBottom: `1px solid ${theme.border.color}`,
                    borderRight: hot ? beamRail : `2px solid ${theme.border.color}`,
                    ...(hot ? { borderLeft: beamRail } : {}),
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.12s',
                  }}
                >
                  <Typography variant="caption" display="block" fontWeight={700} sx={{ fontSize: '0.7rem', letterSpacing: 0.5, color: 'text.secondary' }}>
                    {`WEEK ${idx + 1}`}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                    {fmtWeekRange(ws)}
                  </Typography>
                </TableCell>
              );
            })}
          </TableRow>

          {/* Row 2 — Sa→Fr day labels under each week (weekend/holiday dimmed). */}
          <TableRow>
            {weekColumns.flatMap((ws) => {
              const hot = hoveredWeek === ws;
              return weekDays(ws).map((day, di) => {
                const working = di >= 2 && !holidaySet.has(day);
                return (
                  <TableCell
                    key={day}
                    align="center"
                    onMouseEnter={() => onHoverWeek?.(ws)}
                    sx={{
                      backgroundColor: hot ? alpha(theme.schedule.selection, 0.08) : theme.table.headerBg,
                      py: 0.25,
                      px: 0,
                      width: RAIL_DAY_W,
                      borderBottom: `2px solid ${theme.border.color}`,
                      borderRight: di === 6
                        ? (hot ? beamRail : `2px solid ${theme.border.color}`)
                        : `1px solid ${theme.border.color}`,
                      ...(hot && di === 0 ? { borderLeft: beamRail } : {}),
                    }}
                  >
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.77rem', fontWeight: 700, color: working ? 'text.secondary' : 'text.disabled' }}>
                      {DAY_LABELS[di]}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                      {parseInt(day.slice(8, 10), 10)}
                    </Typography>
                  </TableCell>
                );
              });
            })}
          </TableRow>
        </TableHead>
      </Table>
    </Box>
  );
};
